import { FormGroup, Text } from "@blueprintjs/core";
import { ItemPredicate, ItemRenderer, Select } from "@blueprintjs/select";
import { GithubRefType, OrgId, VcsConnectionType } from "@hex/common";
import React, { useCallback, useEffect } from "react";
import styled from "styled-components";

import { GithubRef, GithubRepository } from "../../generated/graphqlTypes";
import { HexMenuItem, HexTooltip } from "../../hex-components";
import {
  SingleChevronDownIcon,
  TickSmallIcon,
  WarningIcon,
} from "../icons/CustomIcons";

import {
  AddContainer,
  AddIcon,
  InnerButtonContainer,
  SelectButton,
  StyledSpinner,
} from "./AddRepoDialog";
import {
  GetRepositoryQuery,
  useGetRepositoriesQuery,
} from "./queries.generated";

// see https://stackoverflow.com/questions/21262997/what-is-the-format-for-valid-git-repo-names
const REPO_NAME_REGEX = /[A-Za-z0-9._-]+\/[A-Za-z0-9._-]+/;

const SuccessIcon = styled(TickSmallIcon)`
  color: ${({ theme }) => theme.intent.SUCCESS};
`;

const ErrorIcon = styled(WarningIcon)`
  color: ${({ theme }) => theme.intent.DANGER};
`;

export interface RepoSelectorProps {
  orgId: OrgId;
  repoData?: GetRepositoryQuery;
  setSelectedRepo: (repo: GithubRepository | undefined) => void;
  setRepoFetchError: (err: boolean) => void;
  repoDataLoading: boolean;
  selectedRepo?: GithubRepository;
  repoFetchError: boolean;
  setSelectedRefType: (type: GithubRefType) => void;
  connectionType: VcsConnectionType;
  setSelectedRef: (ref: GithubRef | undefined) => void;
}

export const RepoSelector: React.FunctionComponent<RepoSelectorProps> =
  React.memo(function RepoSelector({
    connectionType,
    orgId,
    repoData,
    repoDataLoading,
    repoFetchError,
    selectedRepo,
    setRepoFetchError,
    setSelectedRef,
    setSelectedRefType,
    setSelectedRepo,
  }) {
    const { data: reposData, loading: reposDataLoading } =
      useGetRepositoriesQuery({
        variables: { orgId, connectionType },
      });

    useEffect(() => {
      if (repoData) {
        if (repoData.getGithubRepository) {
          setSelectedRepo(repoData.getGithubRepository);
          setRepoFetchError(false);
        } else {
          setRepoFetchError(true);
        }
      }
    }, [repoData, setRepoFetchError, setSelectedRepo]);

    const repoRenderer: ItemRenderer<GithubRepository> = useCallback(
      (repo, itemProps) => (
        <HexMenuItem
          key={repo.id}
          active={itemProps.modifiers.active}
          disabled={itemProps.modifiers.disabled}
          text={repo.fullName}
          onClick={itemProps.handleClick}
        />
      ),
      [],
    );
    const createNewRepoFromQuery = useCallback((query: string) => {
      const [owner, name] = query.split("/");

      return {
        id: -1,
        owner,
        name,
        fullName: query,
      } as GithubRepository;
    }, []);

    const createNewRepoRenderer = useCallback(
      (
        query: string,
        active: boolean,
        handleClick: (event: React.MouseEvent<HTMLElement, MouseEvent>) => void,
      ) => {
        const valid = REPO_NAME_REGEX.test(query);
        return (
          <HexMenuItem
            key={query}
            active={active}
            disabled={!valid}
            text={
              <AddContainer>
                <AddIcon icon="add" />
                <>{valid ? `Add ${query}` : "Repo format is OWNER/NAME"}</>
              </AddContainer>
            }
            onClick={handleClick}
          />
        );
      },
      [],
    );

    const repoPredicate: ItemPredicate<GithubRepository> = useCallback(
      (query, repo) => {
        const lowerQuery = query.toLowerCase();
        return repo.fullName.toLowerCase().indexOf(lowerQuery) > -1;
      },
      [],
    );

    const onSelectRepo = useCallback(
      (repo: GithubRepository) => {
        setSelectedRepo(repo);
        setSelectedRefType(GithubRefType.BRANCH);
        setSelectedRef(undefined);
      },
      [setSelectedRef, setSelectedRefType, setSelectedRepo],
    );
    return (
      <FormGroup label="Repository">
        <Select<GithubRepository>
          createNewItemFromQuery={
            connectionType === VcsConnectionType.GITHUB
              ? createNewRepoFromQuery
              : undefined
          }
          createNewItemRenderer={
            connectionType === VcsConnectionType.GITHUB
              ? createNewRepoRenderer
              : undefined
          }
          disabled={reposDataLoading}
          inputProps={{
            placeholder:
              connectionType === VcsConnectionType.GITHUB
                ? "Filter or add..."
                : "Filter...",
          }}
          itemPredicate={repoPredicate}
          itemRenderer={repoRenderer}
          items={reposData?.getGithubRepositories.slice() ?? []}
          matchTargetWidth={true}
          popoverProps={{ minimal: true, fill: true }}
          onItemSelect={onSelectRepo}
        >
          <SelectButton
            alignText="left"
            fill={true}
            loading={reposDataLoading}
            rightIcon={<SingleChevronDownIcon />}
            text={
              <InnerButtonContainer>
                {repoDataLoading && <StyledSpinner />}
                {selectedRepo && !repoDataLoading && (
                  <HexTooltip
                    content="Repository not found. Either it does not exist or you do not have permissions to access it."
                    disabled={!repoFetchError}
                  >
                    {selectedRepo?.id == -1 && repoFetchError ? (
                      <ErrorIcon />
                    ) : (
                      <SuccessIcon />
                    )}
                  </HexTooltip>
                )}
                <Text ellipsize={true}>
                  {selectedRepo ? selectedRepo.fullName : "Select..."}
                </Text>
              </InnerButtonContainer>
            }
          />
        </Select>
      </FormGroup>
    );
  });
